import React, { useState, useRef } from 'react';
import { Users, MapPin, Trophy, Instagram, Mail, ArrowLeft, CheckCircle, Calendar, Shield, Award, Upload, X, Eye, EyeOff, Save, Facebook, Youtube } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { communityTeamAPI } from '../../services/community';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const EditTeamProfile = ({ team, onBack, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: team?.name || '',
    description: team?.description || '',
    location: team?.location || '',
    division: team?.division || '',
    founded_year: team?.founded_year || '',
    home_field: team?.home_field || '',
    championships: team?.championships || '',
    awards: team?.awards || '',
    social_instagram: team?.social_instagram || '',
    social_facebook: team?.social_facebook || '',
    social_youtube: team?.social_youtube || '',
    logo_url: team?.logo_url || '',
    contact_email: team?.contact_email || '',
    looking_for_players: team?.looking_for_players || false,
    captain_password: '', // Required for verification
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('type', 'team');

      const response = await fetch(`${API_BASE_URL}/api/uploads/image?type=team`, {
        method: 'POST',
        body: uploadData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      // Cloudinary URLs are absolute, local URLs need API_BASE_URL prefix
      const logoUrl = data.url.startsWith('http') ? data.url : `${API_BASE_URL}${data.url}`;
      setFormData(prev => ({ ...prev, logo_url: logoUrl }));
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Validate captain password
    if (!formData.captain_password) {
      setError('Please enter your captain password to save changes');
      return;
    }

    setLoading(true);

    try {
      const updateData = {
        name: formData.name,
        description: formData.description,
        location: formData.location,
        division: formData.division,
        founded_year: formData.founded_year,
        home_field: formData.home_field,
        championships: formData.championships,
        awards: formData.awards,
        social_instagram: formData.social_instagram,
        social_facebook: formData.social_facebook,
        social_youtube: formData.social_youtube,
        logo_url: formData.logo_url,
        contact_email: formData.contact_email,
        looking_for_players: formData.looking_for_players,
        captain_password: formData.captain_password,
      };

      const response = await fetch(`${API_BASE_URL}/api/community-teams/${team.team_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to update team');
      }

      setSuccess(true);
      // Clear password field after successful save
      setFormData(prev => ({ ...prev, captain_password: '' }));

      // Notify parent component
      if (onSuccess) {
        setTimeout(() => onSuccess(data.team), 1500);
      }
    } catch (err) {
      setError(err.message || 'Failed to update team. Please check your captain password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center text-white/60 hover:text-white mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>
        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
          <Shield className="h-6 w-6 text-[#00d4aa]" />
          Edit Team Profile
        </h2>
        <p className="text-white/60 mt-1">
          Update your team&apos;s information
        </p>
      </div>

      {success && (
        <div className="mb-6 p-4 bg-[#00d4aa]/20 border border-[#00d4aa]/30 rounded-lg flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-[#00d4aa]" />
          <span className="text-[#00d4aa]">Team profile updated successfully!</span>
        </div>
      )}

      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Team Logo */}
        <div className="flex flex-col items-center gap-4 p-6 bg-[#1a1b1e] rounded-lg border border-white/10">
          <div className="w-24 h-24 rounded-xl bg-[#131416] border-2 border-dashed border-white/20 flex items-center justify-center overflow-hidden">
            {formData.logo_url ? (
              <img 
                src={formData.logo_url} 
                alt="Team logo" 
                className="w-full h-full object-cover"
              />
            ) : (
              <Users className="h-10 w-10 text-white/40" />
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              {uploading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Uploading...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  Upload Logo
                </span>
              )}
            </Button>
            {formData.logo_url && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, logo_url: '' }))}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Users className="h-5 w-5 text-[#00d4aa]" />
            Basic Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">Team Name *</label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter team name"
                className="bg-[#1a1b1e] border-white/10 text-white"
                required
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Division</label>
              <select
                name="division"
                value={formData.division}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-md text-white"
              >
                <option value="">Select Division</option>
                <option value="Amateur">Amateur</option>
                <option value="D5">D5</option>
                <option value="D4">D4</option>
                <option value="D3">D3</option>
                <option value="D2">D2</option>
                <option value="D1">D1</option>
                <option value="Pro">Pro</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm text-white/60 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Tell us about your team..."
              rows={3}
              className="w-full px-3 py-2 bg-[#1a1b1e] border border-white/10 rounded-md text-white resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="City, State"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Home Field</label>
              <Input
                name="home_field"
                value={formData.home_field}
                onChange={handleChange}
                placeholder="Your home field"
                className="bg-[#1a1b1e] border-white/10 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">Founded Year</label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  name="founded_year"
                  value={formData.founded_year}
                  onChange={handleChange}
                  placeholder="2020"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Contact Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  type="email"
                  name="contact_email"
                  value={formData.contact_email}
                  onChange={handleChange}
                  placeholder="team@example.com"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Achievements */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#00d4aa]" />
            Achievements
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">Championships</label>
              <Input
                name="championships"
                value={formData.championships}
                onChange={handleChange}
                placeholder="e.g., Regional 2023"
                className="bg-[#1a1b1e] border-white/10 text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Awards</label>
              <div className="relative">
                <Award className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  name="awards"
                  value={formData.awards}
                  onChange={handleChange}
                  placeholder="e.g., Best Sportsmanship"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Instagram className="h-5 w-5 text-[#00d4aa]" />
            Social Media
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-white/60 mb-1">Instagram</label>
              <div className="relative">
                <Instagram className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  name="social_instagram"
                  value={formData.social_instagram}
                  onChange={handleChange}
                  placeholder="@teamhandle"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">Facebook</label>
              <div className="relative">
                <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  name="social_facebook"
                  value={formData.social_facebook}
                  onChange={handleChange}
                  placeholder="facebook.com/team"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-white/60 mb-1">YouTube</label>
              <div className="relative">
                <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  name="social_youtube"
                  value={formData.social_youtube}
                  onChange={handleChange}
                  placeholder="youtube.com/@team"
                  className="bg-[#1a1b1e] border-white/10 text-white pl-10"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Recruitment */}
        <div className="p-4 bg-[#1a1b1e] rounded-lg border border-white/10">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="looking_for_players"
              checked={formData.looking_for_players}
              onChange={handleChange}
              className="w-5 h-5 rounded border-white/20 bg-[#131416] text-[#00d4aa] focus:ring-[#00d4aa]"
            />
            <div>
              <span className="text-white font-medium">Looking for Players</span>
              <p className="text-white/60 text-sm">Show that your team is actively recruiting</p>
            </div>
          </label>
        </div>

        {/* Password Verification */}
        <div className="p-4 bg-[#1a1b1e] rounded-lg border border-white/10">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-3">
            <Shield className="h-5 w-5 text-[#00d4aa]" />
            Verification Required
          </h3>
          <p className="text-white/60 text-sm mb-3">
            Enter your captain password to save changes
          </p>
          <div className="relative">
            <Shield className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              type={showPassword ? 'text' : 'password'}
              name="captain_password"
              value={formData.captain_password}
              onChange={handleChange}
              placeholder="Enter captain password"
              className="bg-[#131416] border-white/10 text-white pl-10 pr-10"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="flex-1 border-white/20 text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
            className="flex-1 bg-[#00d4aa] hover:bg-[#33e0bb] text-[#0a0b0c] font-semibold"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-[#0a0b0c]/30 border-t-[#0a0b0c] rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Save className="h-4 w-4" />
                Save Changes
              </span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditTeamProfile;
